import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, ListChecks, Megaphone, UtensilsCrossed, UserCheck, Cake, PartyPopper, Pin, ChevronDown, CalendarDays, CalendarPlus, CheckSquare, Sparkles, Pin as PinIcon, Rss, Copy, UserPlus, ArrowRight, Siren, Check, Clock, ShoppingCart, Loader2 } from "lucide-react";
import { BrutalCard, SectionTitle, EmptyState } from "@/components/Brutal";
import TaskCard from "@/components/TaskCard";
import QuickTileDialog from "@/components/QuickTileDialog";
import GiftPotDialog from "@/components/GiftPotDialog";
import NewGiftPotDialog from "@/components/NewGiftPotDialog";
import BirthdayCalendarDialog from "@/components/BirthdayCalendarDialog";
import ShoppingDialog from "@/components/ShoppingDialog";
import { Gift } from "lucide-react";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { PersonMultiSelect } from "@/components/Selects";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useConfirm } from "@/components/ConfirmProvider";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const AGENDA_META = {
  taak: { label: "Taak", color: "text-brand-blue", Icon: CheckSquare },
  activiteiten: { label: "Activiteit", color: "text-brand-violet", Icon: Sparkles },
  pindates: { label: "Pin date", color: "text-brand-pink", Icon: PinIcon },
};

export default function Dashboard() {
  const { user, pendingJoins } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const doSendReminder = async () => {
    if (reminderBusy) return;
    if (!reminderAll && reminderSel.length === 0) { toast.error(t("Kies minstens één persoon")); return; }
    const payload = reminderAll ? {} : { user_ids: reminderSel };
    setReminderBusy(true);
    try { await api.post("/notifications/action-reminder", payload); toast.success(t("Melding verstuurd 📣")); setReminderOpen(false); }
    catch { toast.error(t("Versturen mislukt")); }
    finally { setReminderBusy(false); }
  };
  const selectOpenTasksUsers = async () => {
    try {
      const { data: r } = await api.get("/actions/users-with-open-tasks");
      const ids = (r.user_ids || []).filter((id) => users.some((u) => u.user_id === id));
      if (ids.length === 0) { toast.info(t("Niemand heeft nog openstaande taken 🎉")); return; }
      setReminderAll(false); setReminderSel(ids);
      toast.success(`${ids.length} ${t("persoon/personen geselecteerd")}`);
    } catch { toast.error(t("Ophalen mislukt")); }
  };
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderAll, setReminderAll] = useState(true);
  const [reminderSel, setReminderSel] = useState([]);
  const [reminderBusy, setReminderBusy] = useState(false);
  const [tile, setTile] = useState(null);
  const [celebs, setCelebs] = useState([]);
  const [celebDays, setCelebDays] = useState(14);
  const [showAllAnn, setShowAllAnn] = useState(false);
  const [agenda, setAgenda] = useState([]);
  const [feed, setFeed] = useState(null);
  const [feedOpen, setFeedOpen] = useState(false);
  const [giftPotUser, setGiftPotUser] = useState(null);
  const [giftPotId, setGiftPotId] = useState(null);
  const [customPots, setCustomPots] = useState([]);
  const [newPotOpen, setNewPotOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [recentNudges, setRecentNudges] = useState([]);
  const [weekProg, setWeekProg] = useState(null);

  const loadPots = () => api.get("/giftpots").then((r) => setCustomPots(r.data)).catch(() => {});
  useEffect(() => { loadPots(); }, []);

  const openFeed = async () => {
    try { const { data } = await api.get("/agenda/feed-url"); setFeed(data); setFeedOpen(true); }
    catch { toast.error(t("Kon je agenda-abonnement niet laden")); }
  };

  useEffect(() => { api.get(`/celebrations/upcoming?days=${celebDays}`).then((r) => setCelebs(r.data)).catch(() => {}); }, [celebDays]);

  const load = async () => {
    const [d, u] = await Promise.all([api.get("/dashboard"), api.get("/users")]);
    setData(d.data); setUsers(u.data);
    api.get("/agenda/upcoming?days=7").then((r) => setAgenda(r.data)).catch(() => {});
    api.get("/tasks/nudges/recent").then((r) => setRecentNudges(r.data)).catch(() => {});
    api.get("/rewards/week-progress").then((r) => setWeekProg(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const hour = new Date().getHours();
  const greet = hour < 12 ? t("Goedemorgen") : hour < 18 ? t("Goedemiddag") : t("Goedenavond");

  if (!data) return <div className="animate-pulse text-brand-ink/40 font-head font-bold">{t("Laden…")}</div>;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-brand-ink/60 font-semibold">{greet},</p>
          <h1 className="font-head text-4xl font-black tracking-tight">{user?.name?.split(" ")[0]} 👋</h1>
        </div>
      </div>

      {weekProg?.enabled && weekProg?.participating && (weekProg.threshold > 0 || weekProg.reached) && (
        <button data-testid="dash-week-progress" onClick={() => navigate("/punten")}
          className="w-full brutal-sm brutal-press rounded-2xl bg-white p-4 text-start flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <p className="font-head font-black leading-tight flex items-center gap-1.5">💶 {weekProg.title || t("Zakgeld")}</p>
            <span className="text-sm font-black text-brand-violet shrink-0">{weekProg.points}/{weekProg.threshold} {t("pt")}</span>
          </div>
          <div className="h-3 rounded-full bg-brand-ink/10 overflow-hidden">
            <div className={`h-full transition-all ${weekProg.reached ? "bg-brand-green" : "bg-brand-violet"}`}
              style={{ width: `${weekProg.threshold > 0 ? Math.min(100, Math.round((weekProg.points / weekProg.threshold) * 100)) : 100}%` }} />
          </div>
          <p className="text-sm font-semibold text-brand-ink/70 leading-tight">
            {weekProg.reached ? t("Doel gehaald! 🎉") : `${t("Nog")} ${weekProg.remaining} ${t("punten tot je")} ${weekProg.title || t("Zakgeld")}${weekProg.catchup_enabled && (weekProg.pool || []).length > 0 ? ` — ${t("pak een inhaalklus!")} 💪` : ""}`}
          </p>
          {typeof weekProg.achievable === "number" && weekProg.achievable > 0 && (
            <p data-testid="dash-week-achievable" className="text-xs font-bold text-brand-ink/50 leading-tight">
              🎯 {t("Deze week te verdienen")}: {weekProg.achievable} {t("pt")} · {t("jij haalde")} {weekProg.points} {t("pt")} ({weekProg.achieved_pct}%){weekProg.achieved_pct >= 100 ? ` — ${t("alles gedaan wat kon! 👏")}` : ""}
            </p>
          )}
          {weekProg.month && (weekProg.month.threshold > 0 || weekProg.month.reached) && (
            <div className="mt-1 pt-2 border-t-2 border-dashed border-brand-ink/10 flex flex-col gap-2" data-testid="dash-month-progress">
              <div className="flex items-center justify-between gap-2">
                <p className="font-head font-black leading-tight flex items-center gap-1.5">🏆 {weekProg.month.title || t("Maandprijs")}</p>
                <span className="text-sm font-black text-brand-ink/70 shrink-0">{weekProg.month.points}/{weekProg.month.threshold} {t("pt")}</span>
              </div>
              <div className="h-3 rounded-full bg-brand-ink/10 overflow-hidden">
                <div className={`h-full transition-all ${weekProg.month.reached ? "bg-brand-green" : "bg-brand-yellow"}`}
                  style={{ width: `${weekProg.month.threshold > 0 ? Math.min(100, Math.round((weekProg.month.points / weekProg.month.threshold) * 100)) : 100}%` }} />
              </div>
              <p className="text-sm font-semibold text-brand-ink/70 leading-tight">
                {weekProg.month.reached ? t("Maanddoel gehaald! 🎉") : `${t("Nog")} ${weekProg.month.remaining} ${t("punten tot de maandprijs")}${weekProg.month.days_away > 0 ? ` (${t("drempel verlaagd:")} ${weekProg.month.days_away} ${t("dag(en) weg")})` : ""}`}
              </p>
            </div>
          )}
        </button>
      )}

      {isAdmin(user) && (
        <button data-testid="dash-action-reminder" onClick={() => { setReminderAll(true); setReminderSel([]); setReminderOpen(true); }}
          className="w-full brutal-sm brutal-press rounded-2xl bg-brand-violet text-white p-4 text-start flex items-center gap-3">
          <Siren className="w-6 h-6 shrink-0" strokeWidth={2.5} />
          <div>
            <p className="font-head font-black leading-tight">{t("Stuur 'kom in actie'-melding")}</p>
            <p className="text-sm font-semibold opacity-90 leading-tight">{t("Direct push, e-mail én SMS naar iedereen: denk aan jullie taken")}</p>
          </div>
        </button>
      )}
      {isAdmin(user) && pendingJoins > 0 && (
        <button data-testid="dash-pending-joins" onClick={() => navigate("/instellingen")}
          className="w-full brutal-sm brutal-press rounded-2xl bg-brand-pink text-white p-4 flex items-center gap-3 text-start">
          <div className="relative shrink-0">
            <UserPlus className="w-7 h-7" strokeWidth={2.5} />
            <span data-testid="dash-pending-count" className="absolute -top-2 -right-2 bg-white text-brand-pink text-[11px] font-black rounded-full min-w-[20px] h-[20px] px-1 flex items-center justify-center border-2 border-brand-pink">{pendingJoins}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-head font-black leading-tight">{pendingJoins === 1 ? t("1 nieuwe aanmelding") : `${pendingJoins} ${t("nieuwe aanmeldingen")}`}</p>
            <p className="text-sm font-semibold text-white/80">{t("Wacht op jouw goedkeuring")}</p>
          </div>
          <ArrowRight className="w-5 h-5 shrink-0" strokeWidth={2.5} />
        </button>
      )}

      {recentNudges.filter((n) => !n.done || (n.resolved_at && Date.now() - new Date(n.resolved_at).getTime() < 3600000)).length > 0 && (
        <div data-testid="dash-nu-log">
          <SectionTitle><span className="inline-flex items-center gap-2"><Siren className="w-7 h-7 text-red-500" strokeWidth={2.5} />{t("Recente NU!'s")}</span></SectionTitle>
          <div className="space-y-2 mt-2">
            {recentNudges.filter((n) => !n.done || (n.resolved_at && Date.now() - new Date(n.resolved_at).getTime() < 3600000)).slice(0, 5).map((n) => {
              const mins = Math.max(0, Math.round((Date.now() - new Date(n.created_at).getTime()) / 60000));
              const ago = mins < 1 ? t("zojuist") : mins < 60 ? `${mins} ${t("min geleden")}` : `${Math.round(mins / 60)} ${t("uur geleden")}`;
              const later = n.done_delay_min == null ? "" : n.done_delay_min < 1 ? t("meteen") : n.done_delay_min < 60 ? `${n.done_delay_min} ${t("min later")}` : `${Math.round(n.done_delay_min / 60)} ${t("uur later")}`;
              return (
                <div key={n.id} data-testid={`dash-nu-log-${n.id}`} className={`brutal-sm rounded-2xl p-3 flex items-center justify-between gap-2 ${n.done ? "bg-brand-ink/5 opacity-70" : "bg-white"}`}>
                  <div className="min-w-0">
                    <p className={`font-head font-bold leading-tight truncate ${n.done ? "line-through text-brand-ink/50" : ""}`}>{n.task_title}</p>
                    <p className="text-xs text-brand-ink/60 font-bold">{t("door")} {n.actor_name} · {ago}</p>
                    {n.sms_summary && (
                      <p data-testid={`dash-nu-sms-${n.id}`} className={`text-xs font-bold ${n.sms_summary.failed ? "text-red-500" : n.sms_summary.delivered ? "text-brand-green" : "text-brand-ink/50"}`}>
                        💬 SMS: {n.sms_summary.delivered}/{n.sms_summary.total} {t("afgeleverd")}{n.sms_summary.failed ? ` · ${n.sms_summary.failed} ${t("mislukt")}` : ""}
                      </p>
                    )}
                  </div>
                  {n.done ? (
                    <span className="shrink-0 inline-flex items-center gap-1 text-xs font-black rounded-full px-2.5 py-1 bg-brand-ink/10 text-brand-ink/60"><Check className="w-3.5 h-3.5" strokeWidth={3} />{n.done_by_name ? `${n.done_by_name}${later ? ` · ${later}` : ""}` : t("Afgehandeld")}</span>
                  ) : (
                    <span className="shrink-0 inline-flex items-center gap-1 text-xs font-black rounded-full px-2.5 py-1" style={{ background: "#FEF3C7", color: "#92400E" }}><Clock className="w-3.5 h-3.5" strokeWidth={3} />{t("Nog open")}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.announcements?.length > 0 && (() => {
        const isPin = (a) => a.pinned && (!a.pinned_until || a.pinned_until >= new Date().toISOString().slice(0, 10));
        const list = showAllAnn ? data.announcements : data.announcements.slice(0, 2);
        const hidden = data.announcements.length - list.length;
        return (
        <div data-testid="dash-announcements-top">
          <SectionTitle>{t("Mededelingen")}</SectionTitle>
          <div className="space-y-3">
            {list.map((a) => (
              <BrutalCard key={a.id} color="yellow" data-testid={`dash-announce-${a.id}`}>
                <div className="flex items-start gap-2">
                  <Megaphone className="w-5 h-5 mt-0.5" strokeWidth={2.5} />
                  <div>
                    {isPin(a) && <span data-testid={`dash-pinned-${a.id}`} className="inline-flex items-center gap-1 text-[11px] font-black uppercase mb-0.5"><Pin className="w-3 h-3" strokeWidth={2.5} />{t("Vastgezet")}</span>}
                    <p className="font-head font-bold">{a.title}</p>
                    {a.description && <p className="text-sm mt-0.5">{a.description}</p>}
                  </div>
                </div>
              </BrutalCard>
            ))}
          </div>
          {hidden > 0 && <button data-testid="dash-ann-more" onClick={() => setShowAllAnn(true)} className="mt-2 text-sm font-bold underline decoration-2 decoration-brand-yellow flex items-center gap-1"><ChevronDown className="w-4 h-4" strokeWidth={2.5} />{t("Toon meer")} ({hidden})</button>}
          {showAllAnn && data.announcements.length > 2 && <button data-testid="dash-ann-less" onClick={() => setShowAllAnn(false)} className="mt-2 text-sm font-bold underline decoration-2 decoration-brand-yellow">{t("Toon minder")}</button>}
        </div>
        );
      })()}

      <div className="grid grid-cols-2 gap-2">
        <BrutalCard color="orange" className="azure-gradient cursor-pointer brutal-press !p-3" onClick={() => navigate("/taken?scope=alle")} data-testid="stat-open">
          <div className="flex items-center gap-2.5">
            <ListChecks className="w-5 h-5 shrink-0" strokeWidth={2.5} />
            <div><p className="font-head text-2xl font-black leading-none">{data.open_count}</p><p className="font-semibold text-xs mt-0.5">{t("Open taken")}</p></div>
          </div>
        </BrutalCard>
        <BrutalCard color="blue" className="cursor-pointer brutal-press !p-3" onClick={() => navigate("/taken?scope=mij")} data-testid="stat-mine">
          <div className="flex items-center gap-2.5">
            <UserCheck className="w-5 h-5 shrink-0" strokeWidth={2.5} />
            <div><p className="font-head text-2xl font-black leading-none">{data.my_count}</p><p className="font-semibold text-xs mt-0.5">{t("Voor jou")}</p></div>
          </div>
        </BrutalCard>
        {data.shopping_on_dashboard && (
          <BrutalCard color="green" className="cursor-pointer brutal-press !p-3 col-span-2" onClick={() => setShopOpen(true)} data-testid="dashboard-shopping-btn">
            <div className="flex items-center gap-2.5">
              <ShoppingCart className="w-5 h-5 shrink-0" strokeWidth={2.5} />
              <div><p className="font-head text-base font-black leading-none">{t("Boodschappenlijst")}</p><p className="font-semibold text-xs mt-0.5">{t("Tik om te bekijken & toe te voegen")}</p></div>
            </div>
          </BrutalCard>
        )}
      </div>

      {data.quick_tiles?.length > 0 && (
        <div>
          <SectionTitle sub={t("Tik om in te plannen (wanneer + voor wie)")}>{t("Quick Tiles")}</SectionTitle>
          <div className="grid grid-cols-2 gap-2">
            {data.quick_tiles.map((qt) => (
              <button key={qt.id} data-testid={`quick-tile-${qt.id}`}
                onClick={() => setTile(qt)}
                style={{ backgroundColor: "#371F75" }}
                className="brutal brutal-press rounded-xl text-white p-2.5 text-left min-w-0 overflow-hidden flex items-center gap-2">
                <Zap className="w-4 h-4 shrink-0 opacity-80" strokeWidth={3} />
                <p className="font-head font-bold text-sm leading-tight break-words line-clamp-2">{qt.title}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {data.latest_poll && (
        <div>
          <SectionTitle>{t("Wat eten we?")}</SectionTitle>
          <BrutalCard color="pink" className="cursor-pointer brutal-press" onClick={() => navigate("/eten")} data-testid="dashboard-poll">
            <UtensilsCrossed className="w-6 h-6 mb-2" strokeWidth={2.5} />
            <p className="font-head font-bold text-lg">{data.latest_poll.question}</p>
            <p className="text-sm font-semibold mt-1">{Object.keys(data.latest_poll.votes || {}).length} {t("stemmen — tik om te stemmen")}</p>
          </BrutalCard>
        </div>
      )}

      <div data-testid="celebrations-section">
        <SectionTitle sub={t("Verjaardagen & feestjes")}>{t("Aankomende vieringen")}</SectionTitle>
        <div className="flex gap-1.5 mb-2">
          <button data-testid="celeb-range-14" onClick={() => setCelebDays(14)} className={`brutal-sm brutal-press rounded-full px-3 py-1 text-xs font-bold ${celebDays === 14 ? "bg-brand-violet text-white" : "bg-white"}`}>{t("14 dagen")}</button>
          <button data-testid="celeb-range-31" onClick={() => setCelebDays(31)} className={`brutal-sm brutal-press rounded-full px-3 py-1 text-xs font-bold ${celebDays === 31 ? "bg-brand-violet text-white" : "bg-white"}`}>{t("Maand")}</button>
          <button data-testid="new-giftpot-btn-celeb" onClick={() => setNewPotOpen(true)} className="brutal-sm brutal-press rounded-full px-3 py-1 text-xs font-bold bg-brand-violet text-white flex items-center gap-1 ml-auto"><Gift className="w-3.5 h-3.5" strokeWidth={2.5} />{t("Cadeaupot")}</button>
          <button data-testid="birthday-calendar-btn" onClick={() => setCalOpen(true)} className="brutal-sm brutal-press rounded-full px-3 py-1 text-xs font-bold bg-white flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" strokeWidth={2.5} />{t("Kalender")}</button>
        </div>
        {celebs.length > 0 ? (
          <div className="space-y-2">
            {celebs.map((c, i) => (
              <BrutalCard key={i} color={c.type === "verjaardag" ? "pink" : "green"} data-testid={`celeb-${i}`}>
                <div className="flex items-center gap-2.5">
                  {c.type === "verjaardag" ? <Cake className="w-5 h-5 shrink-0" strokeWidth={2.5} /> : <PartyPopper className="w-5 h-5 shrink-0" strokeWidth={2.5} />}
                  <div className="flex-1 min-w-0">
                    <p className="font-head font-bold leading-tight">{c.title}</p>
                    <p className="text-xs font-semibold text-brand-ink/60">{t(c.category)} · {new Date(c.date).toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" })}</p>
                  </div>
                  {c.type === "verjaardag" && c.user_id && c.user_id !== user?.user_id && (
                    <button data-testid={`giftpot-open-${i}`} onClick={() => setGiftPotUser(c.user_id)}
                      className="brutal-sm brutal-press rounded-full px-3 py-1.5 text-xs font-bold bg-brand-violet text-white flex items-center gap-1 shrink-0">
                      <Gift className="w-3.5 h-3.5" strokeWidth={2.5} />{t("Cadeaupot")}
                    </button>
                  )}
                </div>
              </BrutalCard>
            ))}
          </div>
        ) : (
          <p className="text-xs font-semibold text-brand-ink/40 px-1" data-testid="celeb-empty">{t("Geen aankomende vieringen. Je kunt hierboven wel een cadeaupot starten.")}</p>
        )}
      </div>

      {customPots.length > 0 && (
        <div data-testid="giftpots-section">
          <div className="flex items-center justify-between gap-2">
            <SectionTitle sub={t("Samen sparen voor een cadeau")}>{t("Cadeaupotten")}</SectionTitle>
            <button data-testid="new-giftpot-btn" onClick={() => setNewPotOpen(true)}
              className="brutal-sm brutal-press rounded-full px-3 py-1.5 text-xs font-bold bg-brand-violet text-white flex items-center gap-1 shrink-0">
              <Gift className="w-3.5 h-3.5" strokeWidth={2.5} />{t("Nieuwe pot")}
            </button>
          </div>
          <div className="space-y-2">
            {customPots.map((p) => {
              const pct = p.goal ? Math.min(100, Math.round((p.total_money / p.goal) * 100)) : 0;
              return (
                <BrutalCard key={p.id} color={p.closed ? "cream" : "green"} className="cursor-pointer brutal-press" onClick={() => setGiftPotId(p.id)} data-testid={`giftpot-card-${p.id}`}>
                  <div className="flex items-center gap-2.5">
                    <Gift className="w-5 h-5 shrink-0" strokeWidth={2.5} />
                    <div className="flex-1 min-w-0">
                      <p className="font-head font-bold leading-tight truncate">{p.title}{p.closed ? " ✅" : ""}</p>
                      <p className="text-xs font-semibold text-brand-ink/60">
                        {p.recipient_name ? `Voor ${p.recipient_name}` : t("Voor het huis")}
                        {p.event_date ? ` · ${new Date(p.event_date).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}` : ""}
                        {` · ${p.contributors} ${t("deelnemers")}`}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {p.groups && p.groups.length ? p.groups.map((g) => (
                          <span key={g.id} data-testid={`giftpot-group-${p.id}-${g.id}`}
                            className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white"
                            style={{ backgroundColor: g.color || "#8B5CFF" }}>{g.name}</span>
                        )) : (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-brand-ink/10 text-brand-ink/60">{t("Hele huis")}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-head font-black text-sm">€{p.total_money.toFixed(0)}{p.goal ? `/${p.goal.toFixed(0)}` : ""}</p>
                      {p.goal ? <p className="text-[11px] font-bold text-brand-ink/50">{pct}%</p> : null}
                    </div>
                  </div>
                </BrutalCard>
              );
            })}
          </div>
        </div>
      )}

      {agenda.length > 0 && (
        <div data-testid="dash-week-agenda">
          <div className="flex items-center justify-between gap-2">
            <SectionTitle sub={t("Taken, activiteiten & pin dates")}>{t("Deze week")}</SectionTitle>
            <a data-testid="agenda-week-ics" href={`${BACKEND}/api/agenda/week.ics?org=${localStorage.getItem("slackr_org") || ""}&days=7`}
              className="brutal-sm brutal-press rounded-full px-3 py-1.5 text-xs font-bold bg-brand-teal text-white flex items-center gap-1 shrink-0">
              <CalendarPlus className="w-3.5 h-3.5" strokeWidth={2.5} />{t("Hele week in agenda")}
            </a>
          </div>
          <div className="space-y-3">
            {Object.entries(agenda.reduce((acc, it) => { (acc[it.date] = acc[it.date] || []).push(it); return acc; }, {})).map(([date, list]) => {
              const dObj = new Date(date + "T00:00:00");
              const isToday = date === new Date().toISOString().slice(0, 10);
              return (
                <div key={date}>
                  <p className="font-head font-black text-sm text-brand-ink/70 mb-1.5 flex items-center gap-1.5">
                    <CalendarDays className="w-4 h-4" strokeWidth={2.5} />
                    {isToday ? t("Vandaag") : dObj.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "short" })}
                  </p>
                  <div className="space-y-2">
                    {list.map((it) => {
                      const m = AGENDA_META[it.type] || AGENDA_META.taak;
                      const M = m.Icon;
                      return (
                        <BrutalCard key={`${it.type}-${it.id}`} data-testid={`agenda-item-${it.id}`} className="flex items-center gap-3 py-2.5">
                          <M className={`w-5 h-5 shrink-0 ${m.color}`} strokeWidth={2.5} />
                          <div className="flex-1 min-w-0">
                            <p className="font-head font-bold leading-tight truncate">{it.title}</p>
                            <p className="text-xs font-semibold text-brand-ink/50">{t(m.label)}{it.time ? ` · ${it.time}` : ` · ${t("hele dag")}`}</p>
                          </div>
                          <a data-testid={`agenda-ics-${it.id}`} href={`${BACKEND}${it.ics}`} title={t("Zet in agenda")}
                            className="brutal-sm brutal-press rounded-full p-2 bg-brand-teal text-white shrink-0"><CalendarPlus className="w-4 h-4" strokeWidth={2.5} /></a>
                        </BrutalCard>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <button data-testid="agenda-subscribe-btn" onClick={openFeed}
            className="mt-3 w-full brutal-sm brutal-press rounded-xl px-4 py-2.5 text-sm font-bold bg-white flex items-center justify-center gap-2">
            <Rss className="w-4 h-4" strokeWidth={2.5} />{t("Abonneer op je agenda (blijft automatisch bijwerken)")}
          </button>
        </div>
      )}

      <div>
        <SectionTitle>{t("Voor jou")}</SectionTitle>
        {data.my_tasks?.length ? (
          <div className="space-y-3">
            {data.my_tasks.map((t) => <TaskCard key={t.id} task={t} users={users} onChange={load} />)}
          </div>
        ) : <EmptyState icon={ListChecks} title={t("Niks op je lijst")} text={t("Lekker bezig! Je hebt geen openstaande taken.")} />}
      </div>

      {tile && <QuickTileDialog tile={tile} users={users} admin={isAdmin(user)} onClose={() => setTile(null)} onDone={load} />}
      <ShoppingDialog open={shopOpen} onClose={() => setShopOpen(false)} users={users} />
      <GiftPotDialog open={!!giftPotUser} birthdayUserId={giftPotUser} onClose={() => setGiftPotUser(null)} />
      <GiftPotDialog open={!!giftPotId} potId={giftPotId} onClose={() => { setGiftPotId(null); loadPots(); }} onDeleted={loadPots} />
      <NewGiftPotDialog open={newPotOpen} onClose={() => setNewPotOpen(false)} users={users} meId={user?.user_id}
        onCreated={(id) => { loadPots(); setGiftPotId(id); }} />
      <BirthdayCalendarDialog open={calOpen} onClose={() => setCalOpen(false)} />

      <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-w-md" data-testid="action-reminder-dialog">
          <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2"><Siren className="w-5 h-5" strokeWidth={2.5} />{t("Kom in actie sturen")}</DialogTitle></DialogHeader>
          <p className="text-sm text-brand-ink/70">{t("Bericht: 'Denk aan jullie taken, dus kom in actie.' — verstuurd via push, e-mail én SMS.")}</p>
          <button data-testid="reminder-toggle-all" onClick={() => setReminderAll((v) => !v)} className="w-full flex items-center justify-between py-2">
            <span className="font-bold text-sm">👪 {t("Naar iedereen")}</span>
            <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${reminderAll ? "bg-brand-green justify-end" : "bg-brand-ink/20 justify-start"}`}><span className="w-5 h-5 rounded-full bg-white brutal-sm" /></span>
          </button>
          {!reminderAll && (
            <div>
              <button data-testid="reminder-smart-open" onClick={selectOpenTasksUsers}
                className="w-full brutal-sm brutal-press rounded-xl px-3 py-2 mb-2 text-sm font-bold bg-brand-cream flex items-center justify-center gap-2">
                🎯 {t("Alleen wie nog openstaande taken heeft")}
              </button>
              <p className="text-sm font-bold mb-1">{t("Kies wie het bericht krijgt")}</p>
              <PersonMultiSelect users={users} value={reminderSel} onChange={setReminderSel} testid="reminder-people" />
            </div>
          )}
          <button data-testid="reminder-send" disabled={reminderBusy} onClick={doSendReminder} className="w-full brutal-sm brutal-press rounded-xl px-4 py-3 font-head font-black bg-brand-violet text-white mt-2 disabled:opacity-60 flex items-center justify-center gap-2">{reminderBusy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} /> : null}{t("Nu versturen")} 📣</button>
        </DialogContent>
      </Dialog>


      <Dialog open={feedOpen} onOpenChange={setFeedOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-w-md" data-testid="agenda-feed-dialog">
          <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2"><Rss className="w-5 h-5" strokeWidth={2.5} />{t("Abonneer op je agenda")}</DialogTitle></DialogHeader>
          <p className="text-sm text-brand-ink/70">{t("Abonneer je één keer en je taken, activiteiten en pin dates verschijnen automatisch in je eigen agenda — nieuwe items komen er vanzelf bij.")}</p>
          {feed && (
            <div className="space-y-3">
              <a data-testid="agenda-feed-subscribe" href={feed.webcal_url}
                className="w-full brutal-sm brutal-press rounded-xl px-4 py-3 font-bold bg-brand-teal text-white flex items-center justify-center gap-2">
                <CalendarPlus className="w-4 h-4" strokeWidth={2.5} />{t("Abonneren (Apple / Outlook)")}
              </a>
              <div>
                <p className="text-xs font-bold text-brand-ink/60 mb-1">{t("Of kopieer deze link (bv. voor Google Agenda → \"Via URL\"):")}</p>
                <div className="flex items-center gap-2">
                  <input data-testid="agenda-feed-url" readOnly value={feed.https_url}
                    className="flex-1 brutal-sm rounded-xl px-3 py-2 text-xs font-medium outline-none bg-brand-cream truncate" />
                  <button data-testid="agenda-feed-copy" onClick={() => { navigator.clipboard?.writeText(feed.https_url); toast.success(t("Link gekopieerd")); }}
                    className="brutal-sm brutal-press rounded-xl p-2.5 bg-white shrink-0"><Copy className="w-4 h-4" strokeWidth={2.5} /></button>
                </div>
              </div>
              <p className="text-xs text-brand-ink/40">{t("Deze link is persoonlijk — deel 'm niet met anderen.")}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
